/**
 * Each app supplies a merge function that maps the (untrusted) parsed-JSON
 * object onto a fully-typed Settings object, falling back to the supplied
 * defaults for any missing or invalid field.
 */
export type MergeFn<TSettings> = (
  stored: Record<string, unknown>,
  defaults: TSettings,
) => TSettings;

/**
 * Settings store backed by localStorage. The class owns the JSON
 * serialization, parse, and graceful-fallback machinery; the per-app
 * `MergeFn` validates each field against the app's schema (e.g. checkers'
 * `variant` field, backgammon's `difficulty` enum).
 */
export class SettingsManager<TSettings extends object> {
  private settings: TSettings;

  constructor(
    private readonly storageKey: string,
    private readonly defaults: TSettings,
    private readonly merge: MergeFn<TSettings>,
  ) {
    this.settings = this.load();
  }

  get(): TSettings {
    return this.settings;
  }

  update(partial: Partial<TSettings>): void {
    this.settings = { ...this.settings, ...partial };
    this.save();
  }

  reset(): void {
    this.settings = { ...this.defaults };
    this.save();
  }

  private load(): TSettings {
    try {
      const raw = localStorage.getItem(this.storageKey);
      if (raw === null) return { ...this.defaults };
      const parsed: unknown = JSON.parse(raw);
      if (typeof parsed !== "object" || parsed === null) {
        return { ...this.defaults };
      }
      return this.merge(parsed as Record<string, unknown>, this.defaults);
    } catch {
      return { ...this.defaults };
    }
  }

  private save(): void {
    localStorage.setItem(this.storageKey, JSON.stringify(this.settings));
  }
}
